import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import RequestItemGroups from '../../../components/request-builder/RequestItemGroups';
import RequestStatusBadge from '../../../components/request-builder/RequestStatusBadge';
import { normalizeRequestStatus } from '../../../components/request-builder/requestBuilderUtils';
import RequestWorkflowSection from './RequestWorkflowSection';
import { useAuth } from '../../../components/context/AuthContext';
import { useToast } from '../../../components/context/ToastContext';
import repositoryContainer from '../../../services/RepositoryContainer';
import PaymentSummary from '../../../components/payments/PaymentSummary';
import ManualPaymentModal from '../../../components/payments/ManualPaymentModal';
import { canPayAndCompleteManually, canRecordManualPayment } from '../../../components/payments/manualPaymentUtils';
import ConversationButton from '../../../components/chat/ConversationButton';
import ConversationModal from '../../../components/chat/ConversationModal';
import { hasConversationNote } from '../../../utils/chatAccess';
import { isRangePriceFinalized, isRangePriced, needsPriceFinalization } from '../../../utils/pricingUtils';

const formatDate = (value, options = { dateStyle: 'medium' }) => {
  if (!value) return 'Not provided';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-GH', options).format(date);
};

const ActivityRow = ({ icon, label, value, tone = 'primary' }) => {
  const toneClasses = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    secondary: 'bg-secondary/10 text-secondary',
  };

  return (
    <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${toneClasses[tone] || toneClasses.primary}`}>
        <Icon name={icon} size={17} />
      </span>
      <div className="min-w-0 pt-0.5">
        <p className="text-xs text-muted-foreground font-caption">{label}</p>
        <p className="mt-0.5 break-words text-sm font-medium text-foreground font-caption">{value}</p>
      </div>
    </div>
  );
};

const RequestOrderDetailsModal = ({ request, onClose, onRequestUpdated }) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [pendingAction, setPendingAction] = useState('');
  const [reasonAction, setReasonAction] = useState('');
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState('');
  const [paymentRecord, setPaymentRecord] = useState(request);
  const [isManualPaymentOpen, setIsManualPaymentOpen] = useState(false);
  const [isConversationOpen, setIsConversationOpen] = useState(false);
  const [itemOverrides, setItemOverrides] = useState({});

  useEffect(() => {
    setPendingAction('');
    setReasonAction('');
    setReason('');
    setActionError('');
    setPaymentRecord(request);
    setIsManualPaymentOpen(false);
    setIsConversationOpen(false);
    setItemOverrides({});
  }, [request?.id]);

  useEffect(() => {
    setPaymentRecord(request);
  }, [request?.id, request?.amountPaid, request?.balanceDue, request?.isFullyPaid]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !pendingAction && !isConversationOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isConversationOpen, onClose, pendingAction]);

  const normalizedItems = useMemo(() => (request?.items || []).map((item, index) => ({
    ...item,
    id: item.serviceItemId || item.id || `${item.type || 'item'}-${index}`,
    price: Number(item.unitPrice) || 0,
    quantity: Number(item.quantity) || 1,
    type: item.type?.toString().toLowerCase() || 'item',
  })), [request?.items]);
  const items = useMemo(() => normalizedItems.map((item) => ({
    ...item,
    ...(itemOverrides[item.id] || {}),
  })), [itemOverrides, normalizedItems]);

  if (!request) return null;
  const hasSchedule = Boolean(request.requestedDate || request.requestedTime);
  const activityEventCount = [request.createdAt, request.completedAt, request.paymentCompletedAt]
    .filter(Boolean).length;
  const normalizedStatus = normalizeRequestStatus(request.status);
  const isAdmin = user?.roles?.some((role) => String(role).toLowerCase() === 'admin');
  const pricingPending = needsPriceFinalization({ ...request, ...paymentRecord, items });
  const canUseDirectActions = isAdmin
    && !request.workflowInstanceId
    && ['pending', 'inprogress'].includes(normalizedStatus);
  const canRecordBalance = canRecordManualPayment({
    isAdmin,
    record: { ...paymentRecord, items, requiresPriceFinalization: pricingPending },
    fallbackRequiresPayment: user?.company?.requiresPayment,
  });
  const canPayAndComplete = canRecordBalance && canPayAndCompleteManually({
    sourceType: 'bookingRequest',
    status: paymentRecord?.status ?? request.status,
    workflowInstanceId: request.workflowInstanceId,
  });
  const hasExistingNote = hasConversationNote(request.note, request.companyNote);

  const runDirectAction = async (action, actionReason = '') => {
    if (pendingAction) return;
    setPendingAction(action);
    setActionError('');
    try {
      let nextStatus = normalizedStatus;
      if (action === 'inprogress') {
        await repositoryContainer.bookingRequest.updateStatus(request.id, 'inProgress');
        nextStatus = 'inprogress';
      } else if (action === 'completed') {
        await repositoryContainer.bookingRequest.complete(request.id);
        nextStatus = 'completed';
      } else if (action === 'rejected') {
        await repositoryContainer.bookingRequest.updateStatus(request.id, 'rejected', actionReason.trim());
        nextStatus = 'rejected';
      } else if (action === 'cancelled') {
        await repositoryContainer.bookingRequest.cancel(request.id, actionReason.trim() || null);
        nextStatus = 'cancelled';
      }

      await onRequestUpdated?.({
        ...request,
        status: nextStatus,
        companyNote: actionReason.trim() || request.companyNote,
      });
      setReasonAction('');
      setReason('');
      addToast(`Request marked ${nextStatus === 'inprogress' ? 'in progress' : nextStatus}.`, 'success');
    } catch (error) {
      setActionError(error?.data?.message || 'The request could not be updated.');
    } finally {
      setPendingAction('');
    }
  };

  const submitReasonAction = (event) => {
    event.preventDefault();
    if (reasonAction === 'rejected' && !reason.trim()) {
      setActionError('A rejection reason is required.');
      return;
    }
    runDirectAction(reasonAction, reason);
  };

  const handleFinalizePrice = async (item, finalUnitPrice) => {
    const serviceItemId = item.serviceItemId || item.id;
    const raw = await repositoryContainer.bookingRequest.finalizeItemPrice(request.id, serviceItemId, finalUnitPrice);
    const result = raw?.response ?? raw;
    const updatedItems = items.map((current) => (
      (current.serviceItemId || current.id) === serviceItemId
        ? {
            ...current,
            unitPrice: result.finalUnitPrice,
            price: result.finalUnitPrice,
            finalUnitPrice: result.finalUnitPrice,
            isPriceFinalized: true,
            priceFinalizedAt: result.priceFinalizedAt || new Date().toISOString(),
          }
        : current
    ));
    const requiresPriceFinalization = updatedItems.some((current) => (
      isRangePriced(current) && !isRangePriceFinalized(current)
    ));
    setItemOverrides((current) => ({
      ...current,
      [item.id]: updatedItems.find((updated) => updated.id === item.id),
    }));
    setPaymentRecord((current) => ({
      ...current,
      totalAmount: result.totalAmount,
      balanceDue: result.balanceDue,
      requiresPriceFinalization,
    }));
    await onRequestUpdated?.({
      ...request,
      ...result,
      items: updatedItems,
      requiresPriceFinalization,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-3 backdrop-blur-sm sm:p-4"
      onMouseDown={(event) => event.target === event.currentTarget && !pendingAction && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-order-details-title"
        className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
      >
        <header className="flex flex-shrink-0 items-center justify-between border-b border-border px-4 py-3 sm:px-6">
          <div>
            <h2 id="request-order-details-title" className="font-heading text-xl font-semibold text-foreground">
              Request Details
            </h2>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <p className="text-xs text-muted-foreground font-caption">#{request.referenceCode}</p>
              <RequestStatusBadge status={request.status} showIcon={false} compact />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={Boolean(pendingAction)}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Close request details"
          >
            <Icon name="X" size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar sm:p-6">
          <div className="space-y-6">
            <div className="grid items-stretch gap-4 md:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)]">
              <section className="rounded-2xl bg-muted/30 p-4 sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground font-caption">
                    Customer information
                  </p>
                  <h3 className="mt-1 font-heading text-2xl font-semibold text-foreground">{request.clientName}</h3>
                  <div className="mt-3 flex flex-col gap-2 text-sm text-foreground font-caption sm:flex-row sm:flex-wrap sm:gap-x-5">
                    <span className="flex min-w-0 items-center gap-2">
                      <Icon name="Mail" size={15} className="flex-shrink-0 text-muted-foreground" />
                      <span className="break-all">{request.clientEmail || 'Email not provided'}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Icon name="Phone" size={15} className="flex-shrink-0 text-muted-foreground" />
                      <span>{request.clientPhone || 'Phone not provided'}</span>
                    </span>
                  </div>
                  {request.createdAt && (
                    <p className="mt-3 text-sm text-muted-foreground font-caption">
                      Submitted {formatDate(request.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                </div>
              </div>
              </section>

              <section className="rounded-2xl bg-muted/30 p-4 sm:p-5">
                <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Payment</h3>
                {pricingPending && (
                  <div className="mb-3 rounded-lg border border-warning/20 bg-warning/10 p-3">
                    <p className="text-xs font-medium text-foreground font-caption">Finalize all range prices before taking payment or progressing this request.</p>
                  </div>
                )}
                <PaymentSummary
                  record={paymentRecord}
                  totalAmount={paymentRecord?.totalAmount ?? request.totalAmount}
                  onRecordBalance={canRecordBalance ? () => setIsManualPaymentOpen(true) : undefined}
                />
              </section>
            </div>

            <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.75fr)]">
              <div className="p-4 sm:p-5 lg:pr-6">
                <section>
                  <div className="mb-3">
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Requested items</h3>
                      <p className="mt-1 text-xs text-muted-foreground font-caption">
                        {items.length} {items.length === 1 ? 'item' : 'items'} submitted for review
                      </p>
                    </div>
                  </div>
                  {items.length > 0 ? (
                    <div className="rounded-xl bg-muted/20 px-3">
                    <RequestItemGroups
                      items={items}
                      showImages
                      detailsLayout
                      dense
                      standardThumbnail
                      onFinalizePrice={isAdmin ? handleFinalizePrice : undefined}
                    />
                    </div>
                  ) : (
                    <div className="rounded-xl bg-muted/30 p-6 text-center text-sm text-muted-foreground font-caption">
                      No item information is available for this request.
                    </div>
                  )}
                </section>

              </div>

              <aside className="space-y-5 border-t border-border/60 bg-muted/10 p-4 sm:p-5 lg:border-l lg:border-t-0 lg:pl-6">
                {hasSchedule && (
                  <section className="rounded-xl bg-muted/25 p-4">
                    <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Preferred schedule</h3>
                    <div className="divide-y divide-border">
                      {request.requestedDate && (
                        <ActivityRow icon="Calendar" label="Date" value={formatDate(request.requestedDate)} tone="success" />
                      )}
                      {request.requestedTime && (
                        <ActivityRow icon="Clock" label="Time" value={request.requestedTime} />
                      )}
                    </div>
                  </section>
                )}

                {(request.note || request.companyNote) && (
                  <section className="rounded-xl bg-muted/25 p-4">
                    <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Notes</h3>
                    <div className="space-y-4">
                      {request.note && (
                        <div>
                          <p className="text-xs text-muted-foreground font-caption">Customer note</p>
                          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground font-caption">{request.note}</p>
                        </div>
                      )}
                      {request.companyNote && (
                        <div className="rounded-lg bg-primary/10 p-3">
                          <p className="text-xs font-medium text-primary font-caption">Company response</p>
                          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground font-caption">{request.companyNote}</p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                <ConversationButton
                  hasNote={hasExistingNote}
                  onClick={() => setIsConversationOpen(true)}
                />

              </aside>
            </div>

            <RequestWorkflowSection request={request} onRequestUpdated={onRequestUpdated} />

            <details className="group rounded-xl bg-muted/20">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-foreground font-caption [&::-webkit-details-marker]:hidden">
                <span>Request activity &middot; {activityEventCount} {activityEventCount === 1 ? 'event' : 'events'}</span>
                <Icon name="ChevronDown" size={16} className="flex-shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
              </summary>
              <div className="divide-y divide-border border-t border-border/70 px-4 py-3">
                {request.createdAt && (
                  <ActivityRow
                    icon="CalendarPlus"
                    label="Submitted"
                    value={formatDate(request.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  />
                )}
                {request.completedAt && (
                  <ActivityRow
                    icon="CheckCircle"
                    label="Completed"
                    value={formatDate(request.completedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                    tone="success"
                  />
                )}
                {request.paymentCompletedAt && (
                  <ActivityRow
                    icon="BadgeCheck"
                    label="Initial payment confirmed"
                    value={formatDate(request.paymentCompletedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                    tone="secondary"
                  />
                )}
              </div>
            </details>

            {request.history?.length > 0 && (
              <section>
                <h3 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Request history</h3>
                <ol className="space-y-0 px-1 py-2">
                  {request.history.map((entry, index) => (
                    <li key={`${entry.timestamp}-${index}`} className="relative flex gap-3 pb-5 last:pb-0">
                      {index < request.history.length - 1 && (
                        <span className="absolute left-[5px] top-4 h-[calc(100%-0.25rem)] w-px bg-border" aria-hidden="true" />
                      )}
                      <span className="relative z-10 mt-1.5 h-3 w-3 flex-shrink-0 rounded-full border-2 border-card bg-primary" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground font-caption">{entry.action}</p>
                        <p className="mt-1 text-xs text-muted-foreground font-caption">
                          {formatDate(entry.timestamp, { dateStyle: 'medium', timeStyle: 'short' })}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            )}

          </div>
        </div>

        <footer className="flex-shrink-0 border-t border-border bg-card px-4 py-4 sm:px-6">
          {canUseDirectActions && reasonAction ? (
            <form onSubmit={submitReasonAction} className="space-y-3">
              <div>
                <label htmlFor="request-action-reason" className="block text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">
                  {reasonAction === 'rejected' ? 'Rejection reason' : 'Cancellation reason'}
                  {reasonAction === 'rejected' ? ' *' : ' (optional)'}
                </label>
                <textarea
                  id="request-action-reason"
                  rows={2}
                  value={reason}
                  onChange={(event) => { setReason(event.target.value); setActionError(''); }}
                  disabled={Boolean(pendingAction)}
                  className="mt-2 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  placeholder={reasonAction === 'rejected' ? 'Explain why the request cannot proceed...' : 'Add an optional cancellation reason...'}
                />
              </div>
              {actionError && <p className="text-sm text-error font-caption">{actionError}</p>}
              <div className="flex flex-wrap justify-end gap-2">
                <Button type="button" variant="outline" size="sm" disabled={Boolean(pendingAction)} onClick={() => { setReasonAction(''); setActionError(''); }}>
                  Back
                </Button>
                <Button type="submit" variant="danger" size="sm" loading={pendingAction === reasonAction}>
                  {reasonAction === 'rejected' ? 'Reject Request' : 'Cancel Request'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {canUseDirectActions && (
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Request actions</p>
                    {pricingPending && (
                      <p className="mt-1 text-xs text-warning font-caption">Set every final price before progressing or completing this request.</p>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-end gap-2 sm:ml-auto">
                  <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={Boolean(pendingAction)}>Close</Button>
                  {canUseDirectActions && normalizedStatus === 'pending' && !pricingPending && (
                    <Button
                      type="button"
                      size="sm"
                      loading={pendingAction === 'inprogress'}
                      disabled={Boolean(pendingAction)}
                      onClick={() => runDirectAction('inprogress')}
                    >
                      Move to In Progress
                    </Button>
                  )}
                  {canUseDirectActions && normalizedStatus === 'inprogress' && !pricingPending && (
                    <Button
                      type="button"
                      size="sm"
                      variant="success"
                      loading={pendingAction === 'completed'}
                      disabled={Boolean(pendingAction)}
                      onClick={() => runDirectAction('completed')}
                    >
                      Complete Request
                    </Button>
                  )}
                  {canUseDirectActions && (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={Boolean(pendingAction)}
                        onClick={() => { setReasonAction('rejected'); setReason(''); setActionError(''); }}
                      >
                        Reject
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        disabled={Boolean(pendingAction)}
                        onClick={() => { setReasonAction('cancelled'); setReason(''); setActionError(''); }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {actionError && !reasonAction && <p className="text-sm text-error font-caption">{actionError}</p>}
            </div>
          )}
        </footer>
      </div>

      <ManualPaymentModal
        isOpen={isManualPaymentOpen}
        onClose={() => setIsManualPaymentOpen(false)}
        sourceType="bookingRequest"
        sourceId={request.id}
        balanceDue={paymentRecord?.balanceDue}
        canPayAndComplete={canPayAndComplete}
        onRecorded={async (result) => {
          const updated = { ...paymentRecord, ...result };
          setPaymentRecord(updated);
          await onRequestUpdated?.({ ...request, ...result });
        }}
      />

      <ConversationModal
        isOpen={isConversationOpen}
        onClose={() => setIsConversationOpen(false)}
        audience="staff"
        subjectType="bookingRequest"
        subjectId={request.id}
        referenceCode={request.referenceCode}
      />
    </div>
  );
};

export default RequestOrderDetailsModal;
